// frontend/src/pages/Finance/SalesAnalysis.jsx
// ✅ REDISEÑO FUNCIONAL - estilo Argon, datos reales (subtotal + margen + embudo)

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
  Button,
  ButtonGroup,
  Alert,
  Progress,
  Spinner
} from 'reactstrap';
import { salesAnalysisAPI } from '../../services/api';
import SalesComparisonChart from '../../components/Charts/SalesComparisonChart';
import TopPerformersChart from '../../components/Charts/TopPerformersChart';
import { parseDate } from '../../utils/dates';

const SalesAnalysis = () => {
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    return {
      start_date: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
      end_date: now.toISOString().split('T')[0]
    };
  });

  // Datos
  const [summary, setSummary] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [topSellers, setTopSellers] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [topServices, setTopServices] = useState([]);
  const [evolution, setEvolution] = useState([]);

  const granularity = period === 'quarter' || period === 'year' ? 'monthly' : 'daily';

  const loadAnalysisData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { start_date: dateRange.start_date, end_date: dateRange.end_date };

      const [summaryRes, comparisonRes, sellersRes, clientsRes, servicesRes, evolutionRes] =
        await Promise.all([
          salesAnalysisAPI.getSummary(params),
          salesAnalysisAPI.getComparison(params),
          salesAnalysisAPI.getBySeller({ ...params, limit: 10 }),
          salesAnalysisAPI.getByClient({ ...params, limit: 10 }),
          salesAnalysisAPI.getByService({ ...params, limit: 10 }),
          salesAnalysisAPI.getEvolution({ ...params, granularity })
        ]);

      setSummary(summaryRes.data.data);
      setComparison(comparisonRes.data.data);
      setTopSellers(sellersRes.data.data || []);
      setTopClients(clientsRes.data.data || []);
      setTopServices(servicesRes.data.data || []);
      setEvolution(evolutionRes.data.data || []);
      setError(null);
    } catch (err) {
      console.error('Error cargando análisis:', err);
      setError('No se pudieron cargar los datos de análisis');
    } finally {
      setLoading(false);
    }
  }, [dateRange, granularity]);

  useEffect(() => {
    loadAnalysisData();
  }, [loadAnalysisData]);

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    const today = new Date();
    const endDate = new Date();
    let startDate;

    // Períodos CALENDARIO (coinciden con el Dashboard): este mes/trimestre/año a la fecha.
    switch (newPeriod) {
      case 'week':
        startDate = new Date();
        startDate.setDate(today.getDate() - 7);
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'quarter':
        startDate = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        break;
      case 'year':
        startDate = new Date(today.getFullYear(), 0, 1);
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    }

    setDateRange({
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0]
    });
  };

  const getPeriodLabel = () => {
    const labels = { week: 'Esta Semana', month: 'Este Mes', quarter: 'Este Trimestre', year: 'Este Año' };
    return labels[period] || 'Este Mes';
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      maximumFractionDigits: 0
    }).format(amount || 0);

  // subtotal seguro: usa el nuevo campo del backend; si no está, deriva de total/1.16
  const subOr = (subtotal, total) => (subtotal !== undefined && subtotal !== null ? subtotal : (total || 0) / 1.16);

  // % de cambio vs período anterior (los deltas viven en comparison.changes.*)
  const renderDelta = (value) => {
    if (value === undefined || value === null || Number.isNaN(value)) return null;
    const isPositive = value >= 0;
    return (
      <p className="mb-0">
        <span className={`text-${isPositive ? 'success' : 'danger'} text-sm font-weight-bolder`}>
          <i className={`ni ni-bold-${isPositive ? 'up' : 'down'} me-1`}></i>
          {Math.abs(value).toFixed(1)}%
        </span>
        <span className="text-xs text-muted"> vs anterior</span>
      </p>
    );
  };

  const changes = comparison?.changes || {};

  // Etiqueta de cada punto de la evolución (día o mes según granularidad)
  const evolutionData = (evolution || []).map((item) => {
    let label = '';
    if (item.month) {
      const [y, m] = item.month.split('-');
      label = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('es-MX', {
        month: 'short',
        year: '2-digit'
      });
    } else if (item.date) {
      label = parseDate(item.date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
    }
    return {
      label,
      quotes: item.quotes_amount,
      orders: item.orders_amount,
      invoices: item.invoices_amount
    };
  });

  // ============================================
  // ESTADOS: carga inicial y error total
  // ============================================
  if (loading && !summary) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="text-center mt-5">
            <div className="spinner-border text-white" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-white">Cargando análisis de ventas...</p>
          </div>
        </Container>
      </>
    );
  }

  if (error && !summary) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <Card className="shadow border-0">
            <CardBody className="text-center py-5">
              <i className="ni ni-fat-remove text-danger" style={{ fontSize: '3rem' }}></i>
              <h4 className="mt-3">{error}</h4>
              <Button color="primary" className="btn-icon mt-3" onClick={loadAnalysisData}>
                <span className="btn-inner--icon"><i className="ni ni-curved-next"></i></span>
                <span className="btn-inner--text ms-1">Reintentar</span>
              </Button>
            </CardBody>
          </Card>
        </Container>
      </>
    );
  }

  const rent = summary?.rentabilidad || {};
  const cot = summary?.cotizaciones || {};
  const ord = summary?.ordenes || {};
  const fac = summary?.facturas || {};

  const cotSub = subOr(cot.total_subtotal, cot.total_amount);
  const ordSub = subOr(ord.total_subtotal, ord.total_amount);
  const facSub = subOr(fac.total_subtotal, fac.total_amount);
  const facPaidSub = subOr(fac.paid_subtotal, fac.paid_amount);
  const facPendSub = facSub - facPaidSub;

  const ingreso = rent.ingreso ?? ordSub;
  const costos = rent.costos ?? 0;
  const margen = rent.margen ?? ingreso - costos;
  const margenPct = rent.margen_percent ?? (ingreso > 0 ? (margen / ingreso) * 100 : 0);

  // Embudo
  const enviadas = cot.total_count || 0;
  const aceptadas = cot.accepted_count || 0;
  const ordenes = ord.total_count || 0;
  const tasaAceptacion = enviadas > 0 ? (aceptadas / enviadas) * 100 : 0;
  const tasaOrden = enviadas > 0 ? (ordenes / enviadas) * 100 : 0;

  return (
    <>
      {/* HEADER GRADIENT */}
      <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>

      <Container fluid className="py-4">
        {/* HEADER CON FILTROS */}
        <Row className="mb-4">
          <Col md="6">
            <h6 className="text-white mb-0">
              <i className="ni ni-chart-pie-35 me-2"></i>
              Análisis de Ventas - {getPeriodLabel()}
              {loading && summary && (
                <Spinner size="sm" color="light" className="ms-2" />
              )}
            </h6>
            <p className="text-white text-sm opacity-8 mb-0">
              Métricas y análisis detallado del desempeño comercial (montos sin IVA)
            </p>
          </Col>
          <Col md="6" className="text-end">
            <ButtonGroup size="sm">
              <Button color="white" outline={period !== 'week'} onClick={() => handlePeriodChange('week')}>
                <i className="ni ni-time-alarm me-1"></i>Semana
              </Button>
              <Button color="white" outline={period !== 'month'} onClick={() => handlePeriodChange('month')}>
                <i className="ni ni-calendar-grid-58 me-1"></i>Mes
              </Button>
              <Button color="white" outline={period !== 'quarter'} onClick={() => handlePeriodChange('quarter')}>
                <i className="ni ni-chart-bar-32 me-1"></i>Trimestre
              </Button>
              <Button color="white" outline={period !== 'year'} onClick={() => handlePeriodChange('year')}>
                <i className="ni ni-world me-1"></i>Año
              </Button>
            </ButtonGroup>
          </Col>
        </Row>

        {/* Alerta inline (cuando ya hay datos previos) */}
        {error && summary && (
          <Alert color="warning" className="shadow-sm" toggle={() => setError(null)}>
            <span className="alert-icon"><i className="ni ni-support-16"></i></span>
            <span className="alert-text">{error}</span>
            <button className="btn btn-sm btn-outline-dark ms-3" onClick={loadAnalysisData}>
              <i className="ni ni-curved-next me-1"></i>Reintentar
            </button>
          </Alert>
        )}

        {/* ============================================ */}
        {/* CARDS DE ESTADÍSTICAS */}
        {/* ============================================ */}
        {summary && (
          <Row>
            {/* Propuestas */}
            <Col xl="3" sm="6" className="mb-xl-0 mb-4">
              <Card className="shadow border-0">
                <CardBody className="p-3">
                  <Row>
                    <div className="col-8">
                      <div className="numbers">
                        <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">Propuestas Enviadas</p>
                        <p className="text-sm font-weight-bold mb-0 text-dark">{formatCurrency(cotSub)}</p>
                        <small className="text-xs text-muted d-block">+ IVA = {formatCurrency(cot.total_amount)}</small>
                        <p className="mb-0 mt-1"><span className="text-xs text-muted">{enviadas} propuestas</span></p>
                        {renderDelta(changes.quotes_change_percent)}
                      </div>
                    </div>
                    <div className="col-4 text-end">
                      <div className="icon icon-shape bg-gradient-primary shadow-primary text-center rounded-circle">
                        <i className="ni ni-send text-lg opacity-10 text-white" aria-hidden="true"></i>
                      </div>
                    </div>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* Órdenes */}
            <Col xl="3" sm="6" className="mb-xl-0 mb-4">
              <Card className="shadow border-0">
                <CardBody className="p-3">
                  <Row>
                    <div className="col-8">
                      <div className="numbers">
                        <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">Órdenes Recibidas</p>
                        <p className="text-sm font-weight-bold mb-0 text-dark">{formatCurrency(ordSub)}</p>
                        <small className="text-xs text-muted d-block">+ IVA = {formatCurrency(ord.total_amount)}</small>
                        <p className="mb-0 mt-1"><span className="text-xs text-muted">{ordenes} órdenes</span></p>
                        {renderDelta(changes.orders_change_percent)}
                      </div>
                    </div>
                    <div className="col-4 text-end">
                      <div className="icon icon-shape bg-gradient-success shadow-success text-center rounded-circle">
                        <i className="ni ni-bag-17 text-lg opacity-10 text-white" aria-hidden="true"></i>
                      </div>
                    </div>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* Margen / Rentabilidad */}
            <Col xl="3" sm="6" className="mb-xl-0 mb-4">
              <Card className="shadow border-0">
                <CardBody className="p-3">
                  <Row>
                    <div className="col-8">
                      <div className="numbers">
                        <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">Margen Estimado</p>
                        <p className="text-sm font-weight-bold mb-0 text-dark">{formatCurrency(margen)}</p>
                        <small className="text-xs text-muted d-block">de {formatCurrency(ingreso)} en órdenes</small>
                        <p className="mb-0 mt-1">
                          <span className={`text-xs font-weight-bolder text-${margenPct >= 0 ? 'success' : 'danger'}`}>
                            {margenPct.toFixed(1)}% de margen
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="col-4 text-end">
                      <div className="icon icon-shape bg-gradient-info shadow-info text-center rounded-circle">
                        <i className="ni ni-money-coins text-lg opacity-10 text-white" aria-hidden="true"></i>
                      </div>
                    </div>
                  </Row>
                </CardBody>
              </Card>
            </Col>

            {/* Facturado */}
            <Col xl="3" sm="6">
              <Card className="shadow border-0">
                <CardBody className="p-3">
                  <Row>
                    <div className="col-8">
                      <div className="numbers">
                        <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">Facturas Emitidas</p>
                        <p className="text-sm font-weight-bold mb-0 text-dark">{formatCurrency(facSub)}</p>
                        <small className="text-xs text-muted d-block">+ IVA = {formatCurrency(fac.total_amount)}</small>
                        <p className="mb-0 mt-1"><span className="text-xs text-muted">{fac.total_count || 0} facturas</span></p>
                        {renderDelta(changes.revenue_change_percent)}
                      </div>
                    </div>
                    <div className="col-4 text-end">
                      <div className="icon icon-shape bg-gradient-warning shadow-warning text-center rounded-circle">
                        <i className="ni ni-single-copy-04 text-lg opacity-10 text-white" aria-hidden="true"></i>
                      </div>
                    </div>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* ============================================ */}
        {/* EVOLUCIÓN + EMBUDO DE CONVERSIÓN */}
        {/* ============================================ */}
        <Row className="mt-4">
          <Col lg="8" className="mb-lg-0 mb-4">
            <Card className="shadow border-0 z-index-2 h-100">
              <CardHeader className="pb-0 pt-3 bg-transparent border-0">
                <h6 className="text-capitalize mb-0">
                  <i className="ni ni-chart-bar-32 me-2"></i>
                  Evolución de Ventas - {getPeriodLabel()}
                </h6>
                <p className="text-sm mb-0 mt-2">
                  <span className="font-weight-bold">Comparativa</span> de propuestas, órdenes y facturas
                  {granularity === 'monthly' ? ' (por mes)' : ' (por día)'}
                </p>
              </CardHeader>
              <CardBody className="p-3">
                <div className="chart">
                  {evolutionData.length > 0 ? (
                    <SalesComparisonChart data={evolutionData} type={granularity === 'monthly' ? 'monthly' : 'daily'} />
                  ) : (
                    <div
                      style={{ height: '300px' }}
                      className="d-flex flex-column align-items-center justify-content-center bg-gradient-light border-radius-lg"
                    >
                      <i className="ni ni-chart-bar-32 text-muted" style={{ fontSize: '3rem' }}></i>
                      <p className="text-muted mb-0 mt-3 text-center">
                        No hay datos suficientes para mostrar la gráfica
                        <br />
                        <small>Genera más actividad para ver tendencias</small>
                      </p>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Embudo de conversión */}
          <Col lg="4">
            <Card className="shadow border-0 h-100">
              <CardHeader className="pb-0 p-3 border-0">
                <h6 className="mb-0"><i className="ni ni-chart-pie-35 me-2"></i>Embudo de Conversión</h6>
                <p className="text-xs text-muted mb-0 mt-1">{getPeriodLabel().toLowerCase()}</p>
              </CardHeader>
              <CardBody className="p-3">
                {/* Enviadas */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="text-xs font-weight-bold text-uppercase text-muted">
                      <i className="ni ni-send me-1 text-primary"></i>Enviadas
                    </span>
                    <span className="text-sm font-weight-bolder text-dark">{enviadas}</span>
                  </div>
                  <Progress max="100" value={100} className="progress-sm" color="primary" />
                </div>

                {/* Aceptadas */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="text-xs font-weight-bold text-uppercase text-muted">
                      <i className="ni ni-check-bold me-1 text-success"></i>Aceptadas
                    </span>
                    <span className="text-sm font-weight-bolder text-dark">
                      {aceptadas} <small className="text-muted">({tasaAceptacion.toFixed(0)}%)</small>
                    </span>
                  </div>
                  <Progress max="100" value={tasaAceptacion} className="progress-sm" color="success" />
                </div>

                {/* Órdenes */}
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="text-xs font-weight-bold text-uppercase text-muted">
                      <i className="ni ni-bag-17 me-1 text-info"></i>Órdenes
                    </span>
                    <span className="text-sm font-weight-bolder text-dark">
                      {ordenes} <small className="text-muted">({tasaOrden.toFixed(0)}%)</small>
                    </span>
                  </div>
                  <Progress max="100" value={tasaOrden} className="progress-sm" color="info" />
                </div>

                <hr className="my-3" />
                <div className="text-center">
                  <span className="text-xs text-uppercase text-muted d-block">Tasa de conversión</span>
                  <h4 className="font-weight-bolder mb-0 text-info">{tasaOrden.toFixed(1)}%</h4>
                  <span className="text-xs text-muted">propuestas → órdenes</span>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* RENTABILIDAD + COMPARATIVA/POR COBRAR */}
        {/* ============================================ */}
        <Row className="mt-4">
          {/* Rentabilidad */}
          <Col lg="6" className="mb-lg-0 mb-4">
            <Card className="shadow border-0 h-100">
              <CardHeader className="pb-0 p-3 border-0">
                <h6 className="mb-0"><i className="ni ni-money-coins me-2"></i>Rentabilidad de Órdenes</h6>
                <p className="text-xs text-muted mb-0 mt-1">Ingreso, costos y margen (sin IVA)</p>
              </CardHeader>
              <CardBody className="p-3">
                <Row className="text-center mb-3">
                  <Col xs="4">
                    <span className="text-xs text-uppercase text-muted d-block">Ingreso</span>
                    <h6 className="font-weight-bolder mb-0 text-dark">{formatCurrency(ingreso)}</h6>
                  </Col>
                  <Col xs="4">
                    <span className="text-xs text-uppercase text-muted d-block">Costos</span>
                    <h6 className="font-weight-bolder mb-0 text-danger">{formatCurrency(costos)}</h6>
                  </Col>
                  <Col xs="4">
                    <span className="text-xs text-uppercase text-muted d-block">Margen</span>
                    <h6 className="font-weight-bolder mb-0 text-success">{formatCurrency(margen)}</h6>
                  </Col>
                </Row>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="text-xs font-weight-bold text-uppercase text-muted">Margen sobre ingreso</span>
                  <span className={`text-xs font-weight-bolder text-${margenPct >= 0 ? 'success' : 'danger'}`}>
                    {margenPct.toFixed(1)}%
                  </span>
                </div>
                <Progress multi className="progress-lg">
                  <Progress bar color="success" value={ingreso > 0 ? (margen / ingreso) * 100 : 0} />
                  <Progress bar color="danger" value={ingreso > 0 ? (costos / ingreso) * 100 : 0} />
                </Progress>
                <div className="d-flex justify-content-between mt-2">
                  <small className="text-xs text-success"><i className="ni ni-controller me-1"></i>Margen</small>
                  <small className="text-xs text-danger">Costos <i className="ni ni-controller ms-1"></i></small>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Comparativa + Por cobrar */}
          <Col lg="6">
            <Card className="shadow border-0 h-100">
              <CardHeader className="pb-0 p-3 border-0">
                <h6 className="mb-0"><i className="ni ni-align-left-2 me-2"></i>Cotizado vs Vendido vs Facturado</h6>
                <p className="text-xs text-muted mb-0 mt-1">Montos sin IVA del {getPeriodLabel().toLowerCase()}</p>
              </CardHeader>
              <CardBody className="p-3">
                <div className="table-responsive">
                  <Table className="align-items-center mb-0" size="sm">
                    <thead>
                      <tr>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Métrica</th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cotizado</th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Vendido</th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Facturado</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><span className="text-xs font-weight-bold">Monto</span></td>
                        <td className="text-center"><span className="text-xs font-weight-bold text-primary">{formatCurrency(cotSub)}</span></td>
                        <td className="text-center"><span className="text-xs font-weight-bold text-success">{formatCurrency(ordSub)}</span></td>
                        <td className="text-center"><span className="text-xs font-weight-bold text-warning">{formatCurrency(facSub)}</span></td>
                      </tr>
                      <tr>
                        <td><span className="text-xs font-weight-bold">Cantidad</span></td>
                        <td className="text-center"><Badge color="primary" className="badge-sm">{enviadas}</Badge></td>
                        <td className="text-center"><Badge color="success" className="badge-sm">{ordenes}</Badge></td>
                        <td className="text-center"><Badge color="warning" className="badge-sm">{fac.total_count || 0}</Badge></td>
                      </tr>
                      <tr>
                        <td><span className="text-xs font-weight-bold">Ticket Prom.</span></td>
                        <td className="text-center"><span className="text-xs">{formatCurrency(enviadas > 0 ? cotSub / enviadas : 0)}</span></td>
                        <td className="text-center"><span className="text-xs">{formatCurrency(ordenes > 0 ? ordSub / ordenes : 0)}</span></td>
                        <td className="text-center"><span className="text-xs">{formatCurrency((fac.total_count || 0) > 0 ? facSub / fac.total_count : 0)}</span></td>
                      </tr>
                    </tbody>
                  </Table>
                </div>

                {/* Por Cobrar */}
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-xs font-weight-bold text-uppercase text-muted">
                      <i className="ni ni-money-coins me-1"></i>Por Cobrar
                    </span>
                    <span className="text-xs font-weight-bold text-info">{formatCurrency(facPendSub)}</span>
                  </div>
                  <Progress
                    max="100"
                    value={facSub > 0 ? (facPaidSub / facSub) * 100 : 0}
                    className="progress-sm"
                    color="info"
                  />
                  <p className="text-xs text-muted mt-1 mb-0">
                    {facSub > 0 ? `${Math.round((facPaidSub / facSub) * 100)}% cobrado` : '0% cobrado'}
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* TOP VENDEDORES Y CLIENTES */}
        {/* ============================================ */}
        <Row className="mt-4">
          {/* Top Vendedores */}
          <Col lg="6" className="mb-lg-0 mb-4">
            <Card className="shadow border-0 h-100">
              <CardHeader className="pb-0 border-0">
                <h6 className="mb-0"><i className="ni ni-trophy me-2"></i>Top Vendedores</h6>
                <p className="text-xs text-muted mb-0 mt-1">Por monto de órdenes (sin IVA)</p>
              </CardHeader>
              <CardBody className="p-3">
                {topSellers.length > 0 ? (
                  <>
                    <TopPerformersChart
                      data={topSellers.slice(0, 5).map((s) => ({
                        label: s.seller_name,
                        value: subOr(s.orders_subtotal, s.orders_amount)
                      }))}
                      title="Órdenes"
                      colorRgb="45, 206, 137"
                    />
                    <div className="table-responsive mt-3">
                      <Table className="align-items-center mb-0" size="sm">
                        <thead>
                          <tr>
                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Vendedor</th>
                            <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Prop.</th>
                            <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Órd.</th>
                            <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Conv.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topSellers.slice(0, 5).map((seller, index) => (
                            <tr key={seller.seller_id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className={`icon icon-shape icon-xs ${index === 0 ? 'bg-gradient-warning' : index === 1 ? 'bg-gradient-secondary' : 'bg-gradient-info'} shadow text-center rounded-circle me-2`}>
                                    <span className="text-white text-xs font-weight-bold">{index + 1}</span>
                                  </div>
                                  <p className="text-xs font-weight-bold mb-0 text-dark">{seller.seller_name}</p>
                                </div>
                              </td>
                              <td className="text-center"><span className="text-xs font-weight-bold">{seller.quotes_count}</span></td>
                              <td className="text-center"><Badge color="success" className="badge-sm">{seller.orders_count}</Badge></td>
                              <td className="text-center">
                                <Badge color={seller.conversion_rate >= 50 ? 'success' : seller.conversion_rate >= 25 ? 'warning' : 'danger'} className="badge-sm">
                                  {Math.round(seller.conversion_rate)}%
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <i className="ni ni-chart-bar-32 text-muted" style={{ fontSize: '3rem' }}></i>
                    <p className="text-muted mb-0 mt-3">No hay datos de vendedores disponibles</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Top Clientes */}
          <Col lg="6">
            <Card className="shadow border-0 h-100">
              <CardHeader className="pb-0 border-0">
                <h6 className="mb-0"><i className="ni ni-briefcase-24 me-2"></i>Top Clientes</h6>
                <p className="text-xs text-muted mb-0 mt-1">Por monto de órdenes (sin IVA)</p>
              </CardHeader>
              <CardBody className="p-3">
                {topClients.length > 0 ? (
                  <>
                    <TopPerformersChart
                      data={topClients.slice(0, 5).map((c) => ({
                        label: c.client_name,
                        value: subOr(c.total_subtotal, c.total_amount)
                      }))}
                      title="Órdenes"
                      colorRgb="94, 114, 228"
                    />
                    <div className="table-responsive mt-3">
                      <Table className="align-items-center mb-0" size="sm">
                        <thead>
                          <tr>
                            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cliente</th>
                            <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Órd.</th>
                            <th className="text-end text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Total</th>
                            <th className="text-end text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Ticket</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topClients.slice(0, 5).map((client, index) => (
                            <tr key={client.client_id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className={`icon icon-shape icon-xs ${index === 0 ? 'bg-gradient-warning' : index === 1 ? 'bg-gradient-secondary' : 'bg-gradient-info'} shadow text-center rounded-circle me-2`}>
                                    <span className="text-white text-xs font-weight-bold">{index + 1}</span>
                                  </div>
                                  <p className="text-xs font-weight-bold mb-0 text-dark">{client.client_name}</p>
                                </div>
                              </td>
                              <td className="text-center"><Badge color="info" className="badge-sm">{client.orders_count}</Badge></td>
                              <td className="text-end"><span className="text-xs font-weight-bold text-dark">{formatCurrency(subOr(client.total_subtotal, client.total_amount))}</span></td>
                              <td className="text-end"><span className="text-xs text-muted">{formatCurrency(subOr(client.avg_ticket_subtotal, client.avg_ticket))}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <i className="ni ni-chart-bar-32 text-muted" style={{ fontSize: '3rem' }}></i>
                    <p className="text-muted mb-0 mt-3">No hay datos de clientes disponibles</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* TOP SERVICIOS */}
        {/* ============================================ */}
        <Row className="mt-4">
          <Col lg="12">
            <Card className="shadow border-0 mb-4">
              <CardHeader className="pb-0 border-0">
                <Row className="align-items-center">
                  <Col md="6">
                    <h6 className="mb-0"><i className="ni ni-bullet-list-67 me-2"></i>Top Servicios/Productos</h6>
                    <p className="text-xs text-muted mb-0 mt-1">Servicios más vendidos en el período (sin IVA)</p>
                  </Col>
                  <Col md="6" className="text-end">
                    <p className="text-xs text-muted mb-0 mt-2">{topServices.length} servicios registrados</p>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody className="px-0 pt-0 pb-2">
                <div className="table-responsive p-0">
                  <Table className="align-items-center mb-0" hover>
                    <thead>
                      <tr>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">#</th>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Categoría</th>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Servicio</th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cantidad</th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Monto</th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">% del Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topServices.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-5">
                            <i className="ni ni-chart-pie-35 text-muted" style={{ fontSize: '3rem' }}></i>
                            <p className="text-xs text-secondary mb-0 mt-3">No hay datos de servicios disponibles</p>
                          </td>
                        </tr>
                      ) : (
                        (() => {
                          const totalAmount = topServices.reduce((sum, s) => sum + (s.total_amount || 0), 0);
                          return topServices.map((service, index) => {
                            const percentage = totalAmount > 0 ? ((service.total_amount || 0) / totalAmount) * 100 : 0;
                            return (
                              <tr key={index}>
                                <td>
                                  <div className="d-flex px-2 py-1">
                                    <div className={`icon icon-shape icon-sm ${index < 3 ? 'bg-gradient-success' : 'bg-gradient-secondary'} shadow text-center rounded-circle`}>
                                      <span className="text-white text-xs font-weight-bold">{index + 1}</span>
                                    </div>
                                  </div>
                                </td>
                                <td><Badge color="primary" className="badge-sm">{service.category}</Badge></td>
                                <td><p className="text-sm font-weight-bold mb-0 text-dark">{service.service_type}</p></td>
                                <td className="align-middle text-center"><span className="text-sm font-weight-bold text-dark">{service.count}</span></td>
                                <td className="align-middle text-center">
                                  <p className="text-sm font-weight-bold mb-0 text-dark">{formatCurrency((service.total_amount || 0) / 1.16)}</p>
                                  <small className="text-xs text-muted">+ IVA = {formatCurrency(service.total_amount)}</small>
                                </td>
                                <td className="align-middle text-center">
                                  <div className="d-flex align-items-center justify-content-center">
                                    <span className="text-xs font-weight-bold me-2">{percentage.toFixed(1)}%</span>
                                    <div style={{ width: '60px' }}>
                                      <Progress max="100" value={percentage} className="progress-xs" color={index < 3 ? 'success' : 'info'} />
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                        })()
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ============================================ */}
        {/* RESUMEN FINAL */}
        {/* ============================================ */}
        {summary && (
          <Row className="mt-4">
            <Col lg="12">
              <Card className="shadow border-0 bg-gradient-default">
                <CardBody className="p-4">
                  <Row className="align-items-center">
                    <Col md="7">
                      <h6 className="text-white text-uppercase mb-1">Resumen del Período</h6>
                      <p className="text-white mb-0 opacity-8">
                        <strong>{getPeriodLabel()}</strong> - Del{' '}
                        {parseDate(dateRange.start_date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })} al{' '}
                        {parseDate(dateRange.end_date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                    </Col>
                    <Col md="5" className="text-md-end mt-3 mt-md-0">
                      <Row>
                        <Col xs="4" className="text-center">
                          <h5 className="text-white font-weight-bolder mb-0">{ordenes}</h5>
                          <p className="text-white text-xs opacity-8 mb-0">Órdenes</p>
                        </Col>
                        <Col xs="4" className="text-center">
                          <h5 className="text-white font-weight-bolder mb-0">{tasaOrden.toFixed(0)}%</h5>
                          <p className="text-white text-xs opacity-8 mb-0">Conversión</p>
                        </Col>
                        <Col xs="4" className="text-center">
                          <h5 className="text-white font-weight-bolder mb-0">{margenPct.toFixed(0)}%</h5>
                          <p className="text-white text-xs opacity-8 mb-0">Margen</p>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </>
  );
};

export default SalesAnalysis;
